import { useEffect, useState, type FunctionComponent } from 'react';
import { QueryCache, QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '@fff-web/fff-ui-shared';
import Container from './components/container/Container';
import { MappedAnswer, SurveyPageContext } from './components/context/SurveyPageContext';
import withPersonalSurveyDisplayName from './HOC/withPersonalSurveyDisplayName';
import Question from './interfaces/question';
import { answersQuery } from './queries/answersQuery';
import { personalSurveyQuery } from './queries/personalSurveyQuery';
import reactQueryConfig from './queries/reactQueryConfig';
import { mapAnswers } from './utils/mapAnswers';
import { paginate } from './utils/paginator';

const queryCache = new QueryCache();
const queryClient = new QueryClient({ queryCache });

type SurveyProps = {
  lastButtonAction: () => void;
  firstBackButtonAction?: () => void;
  lastPage?: boolean;
};

const WrappedSurvey: FunctionComponent<SurveyProps> = ({
  lastButtonAction,
  firstBackButtonAction,
  lastPage,
}) => {
  const { data: survey, isLoading } = useQuery({
    queryKey: [personalSurveyQuery.key],
    queryFn: () => personalSurveyQuery.query(),
    ...reactQueryConfig,
  });

  const [surveyPage, setSurveyPage] = useState(1);
  const [mappedAnswers, setMappedAnswers] = useState<Map<number, MappedAnswer>>(new Map());
  const [conditionalQuestionIds, setConditionalQuestionIds] = useState<Map<number, number[]>>(
    new Map(),
  );
  const [paginatedSurvey, setPaginatedSurvey] = useState<Map<number, Question[]>>(new Map());

  const { data: answers, isLoading: loadingAnswers } = useQuery({
    queryKey: [answersQuery.key],
    queryFn: async () => {
      if (!survey?.id) return [];
      const data = await answersQuery.query(survey.id);
      return data;
    },
    ...reactQueryConfig,
    enabled: !!survey?.id,
  });
  useEffect(() => {
    answers && setMappedAnswers(mapAnswers({ answers }));
  }, [answers]);

  useEffect(() => {
    const newPaginatedSurvey = survey ? paginate({ survey }) : new Map();
    setPaginatedSurvey(newPaginatedSurvey);
    if (lastPage) setSurveyPage(newPaginatedSurvey.size);
  }, [survey]);

  const increasePage = () => {
    setSurveyPage((prev) => prev + 1);
    return null;
  };

  const decreasePage = () => {
    setSurveyPage((prev) => prev - 1);
    return null;
  };

  const upsertMappedAnswers = (newMappedAnswers: Map<number, MappedAnswer>) => {
    setMappedAnswers(newMappedAnswers);
    return null;
  };

  const setConditionalQuestionId = (questionId: number, conditionalQuestionId: number[]) => {
    conditionalQuestionIds.set(questionId, conditionalQuestionId);
    setConditionalQuestionIds(new Map(conditionalQuestionIds));
  };

  const deleteConditionalQuestionId = (questionId: number) => {
    conditionalQuestionIds.delete(questionId);
    setConditionalQuestionIds(new Map(conditionalQuestionIds));
  };

  if (isLoading || loadingAnswers) return <LoadingSpinner />;

  return (
    <SurveyPageContext.Provider
      value={{
        upsertMappedAnswers,
        mappedAnswers,
        paginatedSurvey,
        surveyPage,
        decreasePage,
        increasePage,
        conditionalQuestionIds,
        setConditionalQuestionId,
        deleteConditionalQuestionId,
        survey,
        lastButtonAction,
        firstBackButtonAction,
      }}
    >
      <Container />
    </SurveyPageContext.Provider>
  );
};

const Survey: FunctionComponent<SurveyProps> = (props) => {
  return (
    <QueryClientProvider client={queryClient}>
      <WrappedSurvey {...props} />
    </QueryClientProvider>
  );
};

const PersonalSurvey = withPersonalSurveyDisplayName(Survey);

export default PersonalSurvey;
